import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable per-page SEO tag injector.
 * Usage:
 *   <Seo
 *     title="Luxury Wedding Websites"
 *     description="..."
 *     path="/s/weddings"
 *     image="https://lockedinforever.com/og.png"
 *     keywords={["wedding website", "..."]}
 *     schema={[{ "@context": "https://schema.org", ... }]}
 *   />
 */
const SITE = "https://lockedinforever.com";
const DEFAULT_IMAGE = `${SITE}/logo.png`;

export default function Seo({
  title,
  rawTitle,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  keywords,
  noindex = false,
  schema,
  type = "website",
}) {
  const fullTitle = rawTitle || (title
    ? `${title} | Locked In Forever`
    : "Locked In Forever | Wedding Websites, Planning & Vendors");
  const url = `${SITE}${path || "/"}`;
  const desc =
    description ||
    "LockedInForever is the luxury wedding platform for modern couples — beautiful wedding websites, digital RSVPs, dress & tux trackers, memory vaults, AI compatibility tests, locked-in couple games, and viral wedding moments.";

  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  // Drop the fallback <title> from index.html while this page's own title is shown (one title for crawlers).
  useEffect(() => {
    const els = [...document.head.querySelectorAll("title[data-static], meta[name='description'][data-static]")];
    els.forEach((e) => e.remove());
    return () => els.forEach((e) => document.head.prepend(e));
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Locked In Forever" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@lockedinforever" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/* ---------- Helpers to build common JSON-LD blocks ---------- */
export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE}${it.url}`,
    })),
  };
}

export function buildFaqSchema(pairs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pairs.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export function buildArticleSchema({ title, description, slug, datePublished = "2026-01-01" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    mainEntityOfPage: `${SITE}/blog/${slug}`,
    publisher: {
      "@type": "Organization",
      name: "LockedInForever",
      logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
    },
    datePublished,
    author: { "@type": "Organization", name: "LockedInForever Editorial" },
  };
}

export function buildLocalBusinessSchema({ city, state, name }) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `LockedInForever — ${name}`,
    description: `${name}, ${state} wedding services, websites, planners, and vendors curated by LockedInForever.`,
    address: {
      "@type": "PostalAddress",
      addressLocality: name,
      addressRegion: state,
      addressCountry: "US",
    },
    url: `${SITE}/locations/${city}`,
  };
}
